import React, { ChangeEvent, useEffect, useState } from 'react';
import { sendApiRequest } from 'lib/api/public';
import { renderField } from 'components/utils';
import { Box, Checkbox, FormControlLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material';
import { CustomField } from 'components/settings/types';
import Group from 'components/common/Group';
import { FormDatePicker } from './form';
import { normalizeCustomFieldValues, parseCustomFieldDateValue, type CustomFieldValues } from './customFields/customFields';

type CustomFieldsFormProps = {
  groupId?: string;
  type: string;
  initialValues?: CustomFieldValues;
  onChange: (values: CustomFieldValues) => void;
};

const CustomFieldsForm = ({ type, initialValues, onChange, groupId }: CustomFieldsFormProps) => {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [values, setValues] = useState<CustomFieldValues>(() => normalizeCustomFieldValues(initialValues));

  const onChangeInput = (field: CustomField, e: ChangeEvent<HTMLInputElement>) => {
    const { _id, fieldLabel } = field;

    const newValues = { ...values, [_id]: { value: e.target.value, label: fieldLabel } };

    setValues(newValues);

    onChange(newValues);
  };

  const onChangeCheckbox = (field: CustomField, e: ChangeEvent<HTMLInputElement>) => {
    const { _id, fieldLabel } = field;
    const newValues = { ...values, [_id]: { value: e.target.checked ? 'yes' : 'no', label: fieldLabel } };

    setValues(newValues);
    onChange(newValues);
  };

  useEffect(() => {
    setValues(normalizeCustomFieldValues(initialValues));
  }, [initialValues]);

  useEffect(() => {
    void sendApiRequest<{ list: CustomField[] }>({
      path: `/custom-fields/list?moduleType=${type}`,
      method: 'GET',
    }).then(response => {
      setFields(response.list);
    });
  }, [type]);

  const renderCustomField = (field: CustomField) => {
    const { _id, fieldType, options = [] } = field;

    const value = values[_id]?.value || (options.length > 0 ? [] : '');

    if (['text', 'number'].includes(fieldType)) {
      return (
        <TextField
          name={_id}
          type={fieldType}
          fullWidth
          size="small"
          onChange={e => onChangeInput(field, e as ChangeEvent<HTMLInputElement>)}
          value={value as string}
        />
      );
    }

    if (fieldType === 'date') {
      const dateValue = parseCustomFieldDateValue(value);

      return (
        <FormDatePicker
          value={dateValue}
          onChange={date => {
            onChangeInput(field, {
              target: { value: date ? date.format('MM-DD-YYYY') : '' },
            } as ChangeEvent<HTMLInputElement>);
          }}
        />
      );
    }

    if (fieldType === 'dropdown') {
      return (
        <Select
          name={_id}
          fullWidth
          onChange={e => onChangeInput(field, e as ChangeEvent<HTMLInputElement>)}
          value={value}
          size="small"
        >
          {options.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (fieldType === 'multiline-text') {
      return (
        <TextField
          name={_id}
          fullWidth
          multiline
          rows={4}
          size="small"
          onChange={e => onChangeInput(field, e as ChangeEvent<HTMLInputElement>)}
          value={value as string}
        />
      );
    }

    if (fieldType === 'checkbox') {
      return (
        <Box display="flex" alignItems="center">
          <span>Yes</span>

          <Checkbox name={_id} onChange={e => onChangeCheckbox(field, e)} checked={value === 'yes'} />
        </Box>
      );
    }

    if (fieldType === 'multi-choice') {
      return (
        <Select
          name={_id}
          fullWidth
          multiple
          onChange={e => onChangeInput(field, e as ChangeEvent<HTMLInputElement>)}
          value={value}
          size="small"
        >
          {options.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (fieldType === 'radios') {
      return (
        <RadioGroup name={_id} onChange={e => onChangeInput(field, e)} value={value}>
          {options.map(option => (
            <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
          ))}
        </RadioGroup>
      );
    }
  };

  if ((fields || []).length === 0) {
    return null;
  }

  return (
    <Group title="Custom Fields" id={groupId}>
      {fields.map(field => (
        <React.Fragment key={field._id}>
          {renderField({
            label: field.fieldLabel,
            renderControl: () => renderCustomField(field),
          })}
        </React.Fragment>
      ))}
    </Group>
  );
};

export default CustomFieldsForm;
